import { useState, useEffect, useCallback } from "react";
import { TodoItem } from "@/types";
import { serializeTodo } from "@/lib/utils/todo";

// Custom hook for localStorage sync
export function useLocalStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(initialValue);

  // State to track if the component is mounted
  const [isMounted, setIsMounted] = useState(false);

  // Read from localStorage and initialize the state after component is mounted
  useEffect(() => {
    setIsMounted(true);
    try {
      const item = localStorage.getItem(key);
      if (!item) return;
      const parsed = JSON.parse(item, (key_0, value) => {
        if (key_0 === "date") return new Date(value);
        return value;
      });

      // Handle specific case for TodoItem array
      if (Array.isArray(parsed) && key === "todos") {
        setStoredValue(parsed.map((item_0: any) => serializeTodo(item_0 as TodoItem)) as T);
      } else {
        setStoredValue(parsed as T);
      }
    } catch (error) {
      console.error("Failed to parse localStorage:", error);
    }
  }, [key]);

  // Return wrapped version of useState's setter function
  const setValue = useCallback((value_0: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function
      const valueToStore = value_0 instanceof Function ? value_0(storedValue) : value_0;
      setStoredValue(valueToStore);

      // Only update localStorage if we're on the client and component is mounted
      if (isMounted) {
        localStorage.setItem(key, JSON.stringify(valueToStore, (key_1, value_1) => {
          if (key_1 === "date" && value_1 instanceof Date) {
            return value_1.toISOString();
          }
          return value_1;
        }));
      }
    } catch (error_0) {
      console.error("Failed to save to localStorage:", error_0);
    }
  }, [key, storedValue, isMounted]);
  return [storedValue, setValue] as const;
}